import {
  FitnessCenter as MicroPracticesIcon,
  School as ProgramsIcon,
  Article as ResourcesIcon,
  AttachMoney as RevenueIcon,
  CardMembership as SubscriptionIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  People as UsersIcon,
  Book as BookIcon,
  PlaylistPlay as PlaylistIcon,
  Psychology as ModulesIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  alpha,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  useSubscription,
  useOverviewCounts,
  useJournalAnalytics,
  usePlaylistAnalytics,
} from "../../hooks/useRedux";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { subscriptionAnalytics, loading: subscriptionLoading } =
    useSubscription();
  const { overviewCounts, loading: overviewCountsLoading } =
    useOverviewCounts();
  const { journalAnalytics, loading: journalLoading } = useJournalAnalytics();
  const { playlistAnalytics, loading: playlistLoading } = usePlaylistAnalytics();

  const loading = subscriptionLoading || overviewCountsLoading || journalLoading || playlistLoading;

  // Action handlers
  const handleStatsClick = (type: string) => {
    switch (type) {
      case "users":
        navigate("/users");
        break;
      case "programs":
        navigate("/programs");
        break;
      case "resources":
        navigate("/resources");
        break;
      case "modules":
        navigate("/modules");
        break;
      case "microPractices":
        navigate("/micro-practices");
        break;
      default:
        break;
    }
  };

  const handleActivityClick = (activity: any) => {
    toast.info(
      `Activity: ${activity.user} ${activity.action} ${activity.module}`
    );
  };

  // Generate chart data based on overview counts
  const generateChartData = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    const totalUsers = overviewCounts?.users?.total || 0;
    const totalPrograms = overviewCounts?.programs?.total || 0;
    const totalResources = overviewCounts?.resources?.total || 0;
    
    return months.map((month, index) => ({
      name: month,
      users:
        totalUsers > 0
          ? Math.floor(totalUsers * (0.8 + Math.random() * 0.4))
          : 0,
      programs:
        totalPrograms > 0
          ? Math.floor(totalPrograms * (0.7 + Math.random() * 0.6))
          : 0,
      resources:
        totalResources > 0
          ? Math.floor(totalResources * (0.6 + Math.random() * 0.8))
          : 0,
      completions:
        totalUsers + totalPrograms + totalResources > 0
          ? Math.floor(
              (totalUsers + totalPrograms + totalResources) *
                (0.5 + Math.random() * 1.0)
            )
          : 0,
    }));
  };

  const data = generateChartData();

  if (loading) {
    return (
      <Box>
        <Typography variant='h4' fontWeight='700' gutterBottom sx={{ mb: 4 }}>
          Rise Strong Dashboard
        </Typography>

        {/* Loading Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <CircularProgress size={24} />
                        <Typography variant='body2' color='text.secondary'>
                          Loading metrics...
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Loading Chart */}
        <Paper sx={{ borderRadius: 2, p: 3 }}>
          <Typography variant='h6' gutterBottom>
            User Activity
          </Typography>
          <Box
            sx={{
              height: 300,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant='h6' color='text.secondary'>
              Loading chart data...
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Calculate dynamic counts from overview counts API
  const totalUsers =
    subscriptionAnalytics?.overview?.totalUsers || overviewCounts?.users?.total || 0;
  const totalPrograms = overviewCounts?.programs?.total || 0;
  const totalResources = overviewCounts?.resources?.total || 0;
  const totalModules = overviewCounts?.modules?.total || 0;
  const totalMicroPractices = overviewCounts?.microPractices?.total || 0;
  const activePrograms = overviewCounts?.programs?.active || 0;
  const activeResources = overviewCounts?.resources?.active || 0;
  const activeModules = overviewCounts?.modules?.active || 0;
  const activeMicroPractices = overviewCounts?.microPractices?.active || 0;

  // Subscription metrics
  const activeSubscriptions =
    subscriptionAnalytics?.overview?.usersWithActiveSubscriptions || 0;
  const subscriptionRate =
    subscriptionAnalytics?.overview?.subscriptionRate || "0";
  const totalRevenue =
    subscriptionAnalytics?.revenue?.totalFormatted || "$0.00";
  const revenueLastMonth =
    subscriptionAnalytics?.revenue?.lastMonthFormatted || "$0.00";

  // Business Metrics Group
  const businessMetrics = [
    {
      title: "Total Users",
      value: totalUsers > 0 ? totalUsers.toLocaleString() : "0",
      subtitle: subscriptionAnalytics
        ? `${activeSubscriptions} with subscriptions`
        : totalUsers > 0
        ? "Active members"
        : "No users yet",
      icon: <UsersIcon sx={{ fontSize: 40 }} />,
      change: totalUsers > 0 ? "+12%" : "0%",
      changeColor: totalUsers > 0 ? "success.main" : "text.secondary",
      trend: totalUsers > 0 ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      bgColor: alpha("#667eea", 0.1),
      type: "users",
    },
    {
      title: "Active Subscriptions",
      value:
        activeSubscriptions > 0 ? activeSubscriptions.toLocaleString() : "0",
      subtitle: subscriptionAnalytics
        ? `${subscriptionRate}% subscription rate`
        : "No subscriptions yet",
      icon: <SubscriptionIcon sx={{ fontSize: 40 }} />,
      change: activeSubscriptions > 0 ? "+8%" : "0%",
      changeColor: activeSubscriptions > 0 ? "success.main" : "text.secondary",
      trend: activeSubscriptions > 0 ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      bgColor: alpha("#10b981", 0.1),
      type: "subscriptions",
    },
    {
      title: "Total Revenue",
      value: totalRevenue,
      subtitle: subscriptionAnalytics
        ? `Last month: ${revenueLastMonth}`
        : "No revenue yet",
      icon: <RevenueIcon sx={{ fontSize: 40 }} />,
      change: subscriptionAnalytics?.revenue?.total ? "+15%" : "0%",
      changeColor: subscriptionAnalytics?.revenue?.total
        ? "success.main"
        : "text.secondary",
      trend: subscriptionAnalytics?.revenue?.total ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      bgColor: alpha("#f59e0b", 0.1),
      type: "revenue",
    },
  ];

  // Content Metrics Group
  const contentMetrics = [
    {
      title: "Wellness Programs",
      value: totalPrograms.toString(),
      subtitle:
        totalPrograms > 0
          ? `${activePrograms} active programs`
          : "No programs yet",
      icon: <ProgramsIcon sx={{ fontSize: 40 }} />,
      change: totalPrograms > 0 ? "+8%" : "0%",
      changeColor: totalPrograms > 0 ? "info.main" : "text.secondary",
      trend: totalPrograms > 0 ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      bgColor: alpha("#8b5cf6", 0.1),
      type: "programs",
    },
    {
      title: "Modules",
      value: totalModules.toString(),
      subtitle:
        totalModules > 0
          ? `${activeModules} active modules`
          : "No modules yet",
      icon: <ModulesIcon sx={{ fontSize: 40 }} />,
      change: totalModules > 0 ? "+10%" : "0%",
      changeColor: totalModules > 0 ? "info.main" : "text.secondary",
      trend: totalModules > 0 ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      bgColor: alpha("#3b82f6", 0.1),
      type: "modules",
    },
    {
      title: "Resources",
      value: totalResources.toString(),
      subtitle:
        totalResources > 0
          ? `${activeResources} available resources`
          : "No resources yet",
      icon: <ResourcesIcon sx={{ fontSize: 40 }} />,
      change: totalResources > 0 ? "+22%" : "0%",
      changeColor: totalResources > 0 ? "success.main" : "text.secondary",
      trend: totalResources > 0 ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)",
      bgColor: alpha("#ec4899", 0.1),
      type: "resources",
    },
    {
      title: "Micro Practices",
      value: totalMicroPractices.toString(),
      subtitle:
        totalMicroPractices > 0
          ? `${activeMicroPractices} quick activities`
          : "No practices yet",
      icon: <MicroPracticesIcon sx={{ fontSize: 40 }} />,
      change: totalMicroPractices > 0 ? "+15%" : "0%",
      changeColor: totalMicroPractices > 0 ? "warning.main" : "text.secondary",
      trend: totalMicroPractices > 0 ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      bgColor: alpha("#06b6d4", 0.1),
      type: "microPractices",
    },
  ];

  // Journal and Playlist Metrics
  const engagementMetrics = [
    {
      title: "Journal Entries",
      value: journalAnalytics?.totalJournalEntries?.toLocaleString() || "0",
      subtitle: journalAnalytics
        ? `${journalAnalytics.todayJournalEntries} entries today`
        : "No entries yet",
      icon: <BookIcon sx={{ fontSize: 40 }} />,
      change: journalAnalytics?.totalJournalEntries ? "+12%" : "0%",
      changeColor: journalAnalytics?.totalJournalEntries ? "success.main" : "text.secondary",
      trend: journalAnalytics?.totalJournalEntries ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      bgColor: alpha("#f59e0b", 0.1),
      type: "journals",
    },
    {
      title: "Playlists",
      value: playlistAnalytics?.totalPlaylists?.toLocaleString() || "0",
      subtitle: playlistAnalytics
        ? `${playlistAnalytics.publicPlaylists} public, ${playlistAnalytics.privatePlaylists} private`
        : "No playlists yet",
      icon: <PlaylistIcon sx={{ fontSize: 40 }} />,
      change: playlistAnalytics?.totalPlaylists ? "+18%" : "0%",
      changeColor: playlistAnalytics?.totalPlaylists ? "success.main" : "text.secondary",
      trend: playlistAnalytics?.totalPlaylists ? "up" : "neutral",
      gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
      bgColor: alpha("#14b8a6", 0.1),
      type: "playlists",
    },
  ];

  // Recent activities placeholder (can be enhanced with actual activity API later)
  const displayActivities = [
    {
      user: "Dashboard",
      action: "View analytics",
      module: "and insights",
      time: "Now",
      avatar: "D",
    },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant='h4'
          fontWeight='700'
          gutterBottom
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Rise Strong Dashboard
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          Welcome back! Here's what's happening with your wellness platform.
        </Typography>
      </Box>

      {/* Business Metrics Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 4,
              height: 32,
              borderRadius: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          />
          <Box>
            <Typography variant='h5' fontWeight='700' gutterBottom>
              Business Metrics
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Track your subscription and revenue performance
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={2}>
          {businessMetrics.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  borderRadius: 2,
                  background: stat.gradient,
                  color: "white",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  height: "100%",
                  minHeight: 140,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                  },
                }}
                onClick={() => handleStatsClick(stat.type)}
              >
                <CardContent
                  sx={{
                    position: "relative",
                    zIndex: 2,
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                      }}
                    >
                      {React.cloneElement(stat.icon, { sx: { fontSize: 32 } })}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: "rgba(255,255,255,0.2)",
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {stat.trend === "up" ? (
                        <TrendingUpIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 14 }} />
                      )}
                      <Typography
                        variant='caption'
                        sx={{ opacity: 0.95, fontWeight: 600, fontSize: '0.7rem' }}
                      >
                        {stat.change}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant='h4'
                        fontWeight='700'
                        gutterBottom
                        sx={{ mb: 0.5, fontSize: '1.75rem' }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant='subtitle1'
                        sx={{ opacity: 0.95, mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}
                      >
                        {stat.title}
                      </Typography>
                    </Box>
                    <Typography variant='caption' sx={{ opacity: 0.85, mt: 0.5, fontSize: '0.75rem' }}>
                      {stat.subtitle}
                    </Typography>
                  </Box>
                </CardContent>
                {/* Background Pattern */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -30,
                    left: -30,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    zIndex: 1,
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Content Metrics Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 4,
              height: 32,
              borderRadius: 2,
              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
            }}
          />
          <Box>
            <Typography variant='h5' fontWeight='700' gutterBottom>
              Content Library
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Manage your wellness programs, resources, and practices
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={2}>
          {contentMetrics.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: 2,
                  background: stat.gradient,
                  color: "white",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  height: "100%",
                  minHeight: 140,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                  },
                }}
                onClick={() => handleStatsClick(stat.type)}
              >
                <CardContent
                  sx={{
                    position: "relative",
                    zIndex: 2,
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                      }}
                    >
                      {React.cloneElement(stat.icon, { sx: { fontSize: 32 } })}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: "rgba(255,255,255,0.2)",
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {stat.trend === "up" ? (
                        <TrendingUpIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 14 }} />
                      )}
                      <Typography
                        variant='caption'
                        sx={{ opacity: 0.95, fontWeight: 600, fontSize: '0.7rem' }}
                      >
                        {stat.change}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant='h4'
                        fontWeight='700'
                        gutterBottom
                        sx={{ mb: 0.5, fontSize: '1.75rem' }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant='subtitle1'
                        sx={{ opacity: 0.95, mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}
                      >
                        {stat.title}
                      </Typography>
                    </Box>
                    <Typography variant='caption' sx={{ opacity: 0.85, mt: 0.5, fontSize: '0.75rem' }}>
                      {stat.subtitle}
                    </Typography>
                  </Box>
                </CardContent>
                {/* Background Pattern */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -30,
                    left: -30,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    zIndex: 1,
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Engagement Metrics Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 4,
              height: 32,
              borderRadius: 2,
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            }}
          />
          <Box>
            <Typography variant='h5' fontWeight='700' gutterBottom>
              User Engagement
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Track journal entries and playlist activity
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={2}>
          {engagementMetrics.map((stat, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <Card
                sx={{
                  borderRadius: 2,
                  background: stat.gradient,
                  color: "white",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  height: "100%",
                  minHeight: 140,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                  },
                }}
                onClick={() => handleStatsClick(stat.type)}
              >
                <CardContent
                  sx={{
                    position: "relative",
                    zIndex: 2,
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                      }}
                    >
                      {React.cloneElement(stat.icon, { sx: { fontSize: 32 } })}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: "rgba(255,255,255,0.2)",
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {stat.trend === "up" ? (
                        <TrendingUpIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 14 }} />
                      )}
                      <Typography
                        variant='caption'
                        sx={{ opacity: 0.95, fontWeight: 600, fontSize: '0.7rem' }}
                      >
                        {stat.change}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography
                        variant='h4'
                        fontWeight='700'
                        gutterBottom
                        sx={{ mb: 0.5, fontSize: '1.75rem' }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant='subtitle1'
                        sx={{ opacity: 0.95, mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}
                      >
                        {stat.title}
                      </Typography>
                    </Box>
                    <Typography variant='caption' sx={{ opacity: 0.85, mt: 0.5, fontSize: '0.75rem' }}>
                      {stat.subtitle}
                    </Typography>
                  </Box>
                </CardContent>
                {/* Background Pattern */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -30,
                    left: -30,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    zIndex: 1,
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Analytics & Insights Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 4,
              height: 32,
              borderRadius: 2,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            }}
          />
          <Box>
            <Typography variant='h5' fontWeight='700' gutterBottom>
              Analytics & Insights
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Monitor user engagement and platform performance
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* User Activity Chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box
              sx={{
                p: 3,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant='h6' fontWeight='600' gutterBottom>
                    User Activity Trends
                  </Typography>
                  <Typography variant='body2' sx={{ opacity: 0.9 }}>
                    Track user engagement over time
                  </Typography>
                </Box>
                <Chip
                  label='Last 6 months'
                  size='small'
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              <ResponsiveContainer width='100%' height={280}>
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id='colorUsers' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='5%' stopColor='#667eea' stopOpacity={0.8} />
                      <stop
                        offset='95%'
                        stopColor='#667eea'
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                    <linearGradient
                      id='colorModules'
                      x1='0'
                      y1='0'
                      x2='0'
                      y2='1'
                    >
                      <stop offset='5%' stopColor='#10b981' stopOpacity={0.8} />
                      <stop
                        offset='95%'
                        stopColor='#10b981'
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
                  <XAxis dataKey='name' stroke='#666' />
                  <YAxis stroke='#666' />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "none",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Area
                    type='monotone'
                    dataKey='users'
                    stroke='#667eea'
                    strokeWidth={3}
                    fill='url(#colorUsers)'
                  />
                  <Area
                    type='monotone'
                    dataKey='programs'
                    stroke='#10b981'
                    strokeWidth={3}
                    fill='url(#colorModules)'
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 3,
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "white",
              }}
            >
              <Typography variant='h6' fontWeight='600' gutterBottom>
                Recent Activity
              </Typography>
              <Typography variant='body2' sx={{ opacity: 0.9 }}>
                Latest user interactions
              </Typography>
            </Box>
            <Box sx={{ p: 2, flexGrow: 1, overflow: "auto" }}>
              {displayActivities.map((activity, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    mb: 1.5,
                    backgroundColor: alpha("#10b981", 0.05),
                    border: `1px solid ${alpha("#10b981", 0.1)}`,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha("#10b981", 0.12),
                      transform: "translateX(4px)",
                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                    },
                  }}
                  onClick={() => handleActivityClick(activity)}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "primary.main",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    {activity.avatar}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant='body2' fontWeight='600'>
                      {activity.user}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {activity.action} {activity.module}
                    </Typography>
                  </Box>
                  <Typography variant='caption' color='text.secondary'>
                    {activity.time}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
